/**
 * The base path or URL the browser calls for the API.
 *
 * One definition, because two consumers have to agree about it: the API client
 * sends the requests, and the Content-Security-Policy built in
 * `next.config.ts` decides whether the browser is allowed to. They did not.
 * Keep the fallback same-origin. Pointing a production bundle at
 * `localhost:6002` means "the user's computer", so a missing deployment value
 * could disclose the bearer token to an unrelated local service. Next proxies
 * this path to the local backend for the documented local production build;
 * nginx owns the same path in deployed environments.
 */
export const DEFAULT_API_BASE_URL = '/api';

const UNSAFE_URL_CHARACTERS = /[\\\u0000-\u0020\u007f]/;

export function resolveApiBaseUrl(
  value: string | undefined = process.env.NEXT_PUBLIC_API_BASE_URL,
): string {
  const trimmed = value?.trim();
  if (!trimmed) return DEFAULT_API_BASE_URL;
  if (UNSAFE_URL_CHARACTERS.test(trimmed)) {
    throw new Error('NEXT_PUBLIC_API_BASE_URL contains unsafe characters');
  }
  // URL.search/hash are empty for bare delimiters (`https://api.example?`),
  // but concatenating an endpoint to either form corrupts or hides its path.
  if (trimmed.includes('?') || trimmed.includes('#')) {
    throw new Error('NEXT_PUBLIC_API_BASE_URL must not contain a query or fragment');
  }

  if (trimmed.startsWith('/')) {
    if (trimmed === '/' || trimmed.startsWith('//')) {
      throw new Error('NEXT_PUBLIC_API_BASE_URL must be an HTTP(S) URL or root-relative path');
    }
    return trimmed.length > 1 ? trimmed.replace(/\/+$/, '') : trimmed;
  }

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    throw new Error('NEXT_PUBLIC_API_BASE_URL must be an HTTP(S) URL or root-relative path');
  }
  if (
    !['http:', 'https:'].includes(parsed.protocol) ||
    parsed.username ||
    parsed.password ||
    parsed.search ||
    parsed.hash
  ) {
    throw new Error('NEXT_PUBLIC_API_BASE_URL must be an HTTP(S) URL without credentials or query');
  }
  return trimmed.length > parsed.origin.length ? trimmed.replace(/\/+$/, '') : parsed.origin;
}

export function buildApiUrl(endpoint: string, baseUrl: string = resolveApiBaseUrl()): string {
  if (!endpoint.startsWith('/') || endpoint.startsWith('//')) {
    throw new Error('API endpoint must be a root-relative path');
  }
  return `${baseUrl}${endpoint}`;
}
